import { useEffect, useState } from 'react'

import type { DeviceName, DeviceWarning } from './deviceWarning'

import { blockedWarning } from './deviceWarning'

export type DevicePermissionState = 'prompt' | 'granted' | 'denied' | 'unknown'

export const permissionWarning = (
  device: DeviceName,
  state: DevicePermissionState,
  current?: DeviceWarning
): DeviceWarning | undefined => {
  if (state === 'denied') {
    return blockedWarning(device)
  }

  if (state === 'granted' && current?.reason === 'blocked') {
    return undefined
  }

  return current
}

export const queryDevicePermission = async (device: DeviceName): Promise<DevicePermissionState> => {
  try {
    const status = await navigator.permissions.query({ name: device as PermissionName })

    return status.state
  } catch {
    return 'unknown'
  }
}

export const useDevicePermission = (device: DeviceName): DevicePermissionState => {
  const [state, setState] = useState<DevicePermissionState>('unknown')

  useEffect(() => {
    let status: PermissionStatus | undefined
    let isCancelled = false

    navigator.permissions
      ?.query({ name: device as PermissionName })
      .then((permission) => {
        if (isCancelled) {
          return
        }

        status = permission
        setState(permission.state)

        permission.onchange = () => setState(permission.state)
      })
      .catch(() => undefined)

    return () => {
      isCancelled = true

      if (status) {
        status.onchange = null
      }
    }
  }, [device])

  return state
}
